import React, { useState, useEffect, useCallback } from 'react';
import { ethers } from 'ethers';
import { useWeb3React } from '@web3-react/core';
import './LicensePanel.css';

const LICENSE_CONTRACT = "0x3bDcE545afEe5BA680a3CBA2c5b5E19a2D8d8d6E";
const CHAIN_ID = 84532; // Base Sepolia

const LICENSE_ABI = [
  "function purchaseLicense() external payable",
  "function renewLicense() external payable",
  "function hasLicense(address holder) external view returns (bool)",
  "function licenses(address) external view returns (address holder, uint256 issuedAt, uint256 expiresAt, uint8 status)",
  "function licensePrice() external view returns (uint256)",
  "function licenseDuration() external view returns (uint256)",
  "function owner() external view returns (address)",
  "function issueLicense(address holder) external",
  "function revokeLicense(address holder) external",
  "function setLicensePrice(uint256 newPrice) external",
  "function setLicenseDuration(uint256 newDuration) external",
  "function pause() external",
  "function unpause() external",
  "function paused() external view returns (bool)",
  "function withdraw() external"
];

export default function LicensePanel() {
  const { account, library, chainId } = useWeb3React();
  const [licenseData, setLicenseData] = useState(null);
  const [isOwner, setIsOwner] = useState(false);
  const [config, setConfig] = useState({ price: '0.01', duration: 365, paused: false });
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  const [activeTab, setActiveTab] = useState('user'); // 'user' or 'admin'

  // Admin forms
  const [issueAddress, setIssueAddress] = useState('');
  const [revokeAddress, setRevokeAddress] = useState('');
  const [newPrice, setNewPrice] = useState('');

  const loadLicenseData = useCallback(async () => {
    try {
      setLoading(true);
      const contract = new ethers.Contract(LICENSE_CONTRACT, LICENSE_ABI, library);

      // Load config
      const price = await contract.licensePrice();
      const duration = await contract.licenseDuration();
      const paused = await contract.paused();

      setConfig({
        price: ethers.formatEther(price),
        duration: Number(duration) / 86400,
        paused
      });

      // Load user license
      const hasLicense = await contract.hasLicense(account);

      if (hasLicense) {
        const license = await contract.licenses(account);
        const expiresAt = new Date(Number(license.expiresAt) * 1000);
        const issuedAt = new Date(Number(license.issuedAt) * 1000);
        const status = ['None', 'Active', 'Expired', 'Revoked'][license.status];

        setLicenseData({
          hasLicense: true,
          expiresAt,
          issuedAt,
          status,
          daysRemaining: Math.max(0, Math.ceil((expiresAt - new Date()) / (1000 * 60 * 60 * 24)))
        });
      } else {
        setLicenseData({ hasLicense: false });
      }

      setLoading(false);
    } catch (error) {
      console.error('Error loading license:', error);
      setMessage({ text: 'Failed to load license data', type: 'error' });
      setLoading(false);
    }
  }, [account, library]);

  const checkOwnership = useCallback(async () => {
    try {
      const contract = new ethers.Contract(LICENSE_CONTRACT, LICENSE_ABI, library);
      const owner = await contract.owner();
      setIsOwner(owner.toLowerCase() === account.toLowerCase());
    } catch (error) {
      console.error('Error checking ownership:', error);
    }
  }, [account, library]);

  useEffect(() => {
    if (account && library && chainId === CHAIN_ID) {
      loadLicenseData();
      checkOwnership();
    }
  }, [account, library, chainId, loadLicenseData, checkOwnership]);

  const purchaseLicense = async () => {
    try {
      setProcessing(true);
      setMessage({ text: 'Sending transaction...', type: 'info' });

      const signer = library.getSigner();
      const contract = new ethers.Contract(LICENSE_CONTRACT, LICENSE_ABI, signer);

      const tx = await contract.purchaseLicense({
        value: ethers.parseEther(config.price)
      });

      setMessage({ text: 'Waiting for confirmation...', type: 'info' });
      await tx.wait();

      setMessage({ text: 'License purchased successfully! 🎉', type: 'success' });
      await loadLicenseData();
      setProcessing(false);
    } catch (error) {
      console.error('Purchase error:', error);
      setMessage({
        text: `Failed to purchase: ${error.message.substring(0, 100)}`,
        type: 'error'
      });
      setProcessing(false);
    }
  };

  const renewLicense = async () => {
    try {
      setProcessing(true);
      setMessage({ text: 'Renewing license...', type: 'info' });

      const signer = library.getSigner();
      const contract = new ethers.Contract(LICENSE_CONTRACT, LICENSE_ABI, signer);

      const tx = await contract.renewLicense({
        value: ethers.parseEther(config.price)
      });

      await tx.wait();

      setMessage({ text: 'License renewed successfully! 🎉', type: 'success' });
      await loadLicenseData();
      setProcessing(false);
    } catch (error) {
      console.error('Renew error:', error);
      setMessage({
        text: `Failed to renew: ${error.message.substring(0, 100)}`,
        type: 'error'
      });
      setProcessing(false);
    }
  };

  const issueLicense = async () => {
    if (!ethers.isAddress(issueAddress)) {
      setMessage({ text: 'Invalid address', type: 'error' });
      return;
    }

    try {
      setProcessing(true);
      setMessage({ text: 'Issuing license...', type: 'info' });

      const signer = library.getSigner();
      const contract = new ethers.Contract(LICENSE_CONTRACT, LICENSE_ABI, signer);

      const tx = await contract.issueLicense(issueAddress);
      await tx.wait();

      setMessage({ text: `License issued to ${issueAddress}`, type: 'success' });
      setIssueAddress('');
      setProcessing(false);
    } catch (error) {
      console.error('Issue error:', error);
      setMessage({ text: `Failed: ${error.message.substring(0, 100)}`, type: 'error' });
      setProcessing(false);
    }
  };

  const revokeLicense = async () => {
    if (!ethers.isAddress(revokeAddress)) {
      setMessage({ text: 'Invalid address', type: 'error' });
      return;
    }

    try {
      setProcessing(true);
      setMessage({ text: 'Revoking license...', type: 'info' });

      const signer = library.getSigner();
      const contract = new ethers.Contract(LICENSE_CONTRACT, LICENSE_ABI, signer);

      const tx = await contract.revokeLicense(revokeAddress);
      await tx.wait();

      setMessage({ text: `License revoked from ${revokeAddress}`, type: 'success' });
      setRevokeAddress('');
      setProcessing(false);
    } catch (error) {
      console.error('Revoke error:', error);
      setMessage({ text: `Failed: ${error.message.substring(0, 100)}`, type: 'error' });
      setProcessing(false);
    }
  };

  const updatePrice = async () => {
    try {
      setProcessing(true);
      setMessage({ text: 'Updating price...', type: 'info' });

      const signer = library.getSigner();
      const contract = new ethers.Contract(LICENSE_CONTRACT, LICENSE_ABI, signer);

      const priceWei = ethers.parseEther(newPrice);
      const tx = await contract.setLicensePrice(priceWei);
      await tx.wait();

      setMessage({ text: `Price updated to ${newPrice} ETH`, type: 'success' });
      await loadLicenseData();
      setNewPrice('');
      setProcessing(false);
    } catch (error) {
      console.error('Price update error:', error);
      setMessage({ text: `Failed: ${error.message.substring(0, 100)}`, type: 'error' });
      setProcessing(false);
    }
  };

  if (!account || chainId !== CHAIN_ID) {
    return (
      <div className="license-panel">
        <div className="license-warning">
          <h3>⚠️ Wrong Network</h3>
          <p>Please connect to Base Sepolia (Chain ID: 84532)</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="license-panel">
        <div className="license-loading">Loading license status...</div>
      </div>
    );
  }

  return (
    <div className="license-panel">
      <div className="license-header">
        <h2>🎫 License Management</h2>
        {isOwner && (
          <div className="tab-switcher">
            <button
              className={activeTab === 'user' ? 'active' : ''}
              onClick={() => setActiveTab('user')}
            >
              My License
            </button>
            <button
              className={activeTab === 'admin' ? 'active' : ''}
              onClick={() => setActiveTab('admin')}
            >
              Admin Panel
            </button>
          </div>
        )}
      </div>

      {activeTab === 'user' && (
        <div className="license-content">
          {licenseData?.hasLicense ? (
            <div className="license-card active">
              <div className="license-status">
                <span className="status-badge active">✅ Active License</span>
                <div className="license-details">
                  <div className="detail-row">
                    <span>Issued:</span>
                    <span>{licenseData.issuedAt.toLocaleDateString()}</span>
                  </div>
                  <div className="detail-row">
                    <span>Expires:</span>
                    <span>{licenseData.expiresAt.toLocaleDateString()}</span>
                  </div>
                  <div className="detail-row">
                    <span>Days Remaining:</span>
                    <span className={licenseData.daysRemaining < 30 ? 'expiring' : ''}>
                      {licenseData.daysRemaining} days
                    </span>
                  </div>
                </div>
              </div>

              {licenseData.daysRemaining < 30 && (
                <div className="renewal-notice">
                  ⚠️ Your license expires soon. Renew to continue using admin features.
                </div>
              )}

              <button
                className="btn-primary"
                onClick={renewLicense}
                disabled={processing}
              >
                {processing ? 'Processing...' : `Renew License (${config.price} ETH)`}
              </button>
            </div>
          ) : (
            <div className="license-card inactive">
              <div className="license-status">
                <span className="status-badge inactive">❌ No License</span>
              </div>

              <div className="license-benefits">
                <h4>Admin License Benefits:</h4>
                <ul>
                  <li>✅ Create and manage events</li>
                  <li>✅ Access event analytics</li>
                  <li>✅ Manage ticket types</li>
                  <li>✅ View detailed reports</li>
                </ul>
              </div>

              <div className="license-pricing">
                <div className="price">{config.price} ETH</div>
                <div className="duration">Valid for {config.duration} days</div>
              </div>

              <button
                className="btn-primary"
                onClick={purchaseLicense}
                disabled={processing}
              >
                {processing ? 'Processing...' : `Purchase License (${config.price} ETH)`}
              </button>
            </div>
          )}

          {message.text && (
            <div className={`message ${message.type}`}>
              {message.text}
            </div>
          )}
        </div>
      )}

      {activeTab === 'admin' && isOwner && (
        <div className="admin-content">
          <div className="admin-section">
            <h4>Issue Free License</h4>
            <div className="admin-form">
              <input
                type="text"
                placeholder="0x..."
                value={issueAddress}
                onChange={(e) => setIssueAddress(e.target.value)}
              />
              <button
                onClick={issueLicense}
                disabled={processing || !issueAddress}
                className="btn-secondary"
              >
                Issue License
              </button>
            </div>
          </div>

          <div className="admin-section">
            <h4>Revoke License</h4>
            <div className="admin-form">
              <input
                type="text"
                placeholder="0x..."
                value={revokeAddress}
                onChange={(e) => setRevokeAddress(e.target.value)}
              />
              <button
                onClick={revokeLicense}
                disabled={processing || !revokeAddress}
                className="btn-danger"
              >
                Revoke License
              </button>
            </div>
          </div>

          <div className="admin-section">
            <h4>Update Price</h4>
            <div className="admin-form">
              <input
                type="number"
                step="0.001"
                placeholder="0.01"
                value={newPrice}
                onChange={(e) => setNewPrice(e.target.value)}
              />
              <button
                onClick={updatePrice}
                disabled={processing || !newPrice}
                className="btn-secondary"
              >
                Update Price
              </button>
            </div>
          </div>

          {message.text && (
            <div className={`message ${message.type}`}>
              {message.text}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
